import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const FrontOfficeReports = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [occupancyReport, setOccupancyReport] = useState([]);
  const [revenueData, setRevenueData] = useState({ summary: {}, byRoomType: [] });
  const [allBookings, setAllBookings] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    purple: '#8b5cf6',
    pink: '#ec4899',
    orange: '#f97316'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchOccupancyReport(),
      fetchRevenueReport(),
      fetchAllBookings(),
      fetchAllPayments()
    ]);
    setLoading(false);
  };

  const fetchOccupancyReport = async () => {
    try {
      const data = await apiGet(
        `/admin/front-office/reports/occupancy?startDate=${dateRange.start}&endDate=${dateRange.end}`
      );
      if (data.success) setOccupancyReport(data.data);
    } catch (error) {
      console.error('Error fetching occupancy report:', error);
    }
  };

  const fetchRevenueReport = async () => {
    try {
      const data = await apiGet('/admin/front-office/reports/revenue');
      if (data.success) setRevenueData(data.data);
    } catch (error) {
      console.error('Error fetching revenue report:', error);
    }
  };

  const fetchAllBookings = async () => {
    try {
      const data = await apiGet('/admin/bookings');
      if (data.success) setAllBookings(data.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchAllPayments = async () => {
    try {
      const data = await apiGet('/admin/revenue/today');
      if (data.success && data.data.payments) {
        setAllPayments(data.data.payments);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  // Calculate payment mode breakdown
  const getPaymentModeBreakdown = () => {
    const modes = {
      cash: 0,
      upi: 0,
      card: 0,
      bank_transfer: 0,
      other: 0
    };

    allPayments.forEach(payment => {
      const method = payment.payment_method?.toLowerCase() || 'other';
      if (modes.hasOwnProperty(method)) {
        modes[method] += parseFloat(payment.amount || 0);
      } else {
        modes.other += parseFloat(payment.amount || 0);
      }
    });

    return modes;
  };

  // Calculate booking source breakdown
  const getBookingSourceBreakdown = () => {
    const sources = {
      direct: 0,
      channel_manager: 0,
      walk_in: 0,
      other: 0
    };

    allBookings.forEach(booking => {
      const source = booking.booking_type?.toLowerCase() || booking.source?.toLowerCase() || 'other';
      if (sources.hasOwnProperty(source)) {
        sources[source]++;
      } else {
        sources.other++;
      }
    });

    return sources;
  };

  // Calculate status breakdown
  const getBookingStatusBreakdown = () => {
    const statuses = {
      confirmed: 0,
      checked_in: 0,
      checked_out: 0,
      pending: 0,
      cancelled: 0
    };

    allBookings.forEach(booking => {
      const status = booking.reservation_status?.toLowerCase() || booking.status?.toLowerCase() || 'pending';
      if (statuses.hasOwnProperty(status)) {
        statuses[status]++;
      }
    });

    return statuses;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '20px' : '28px',
      padding: isMobile ? '0 8px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '22px' : isTablet ? '26px' : '32px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: '8px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '24px' : '32px',
      padding: isMobile ? '0 8px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 16px rgba(45, 80, 22, 0.08)',
      border: `2px solid ${colors.mediumGray}`,
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    statValue: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '800',
      marginBottom: '6px',
      lineHeight: '1'
    },
    statLabel: {
      fontSize: isMobile ? '10px' : '11px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '700',
      letterSpacing: '0.8px'
    },
    statIcon: {
      fontSize: isMobile ? '20px' : '24px',
      marginBottom: '8px',
      display: 'block'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '18px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.1)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 8px 16px 8px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '14px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '12px' : '14px',
      borderBottom: `2px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '11px' : '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    tableCell: {
      padding: isMobile ? '12px' : '14px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    input: {
      padding: '12px 14px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      fontSize: '14px',
      fontFamily: 'inherit'
    },
    chartBar: {
      height: isMobile ? '35px' : '40px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '14px',
      color: colors.white,
      fontWeight: '700',
      fontSize: isMobile ? '11px' : '13px',
      marginBottom: '12px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    pieChart: {
      width: isMobile ? '180px' : '220px',
      height: isMobile ? '180px' : '220px',
      borderRadius: '50%',
      margin: '0 auto',
      position: 'relative',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    legend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      justifyContent: 'center',
      marginTop: '20px'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600'
    },
    legendDot: {
      width: '14px',
      height: '14px',
      borderRadius: '50%'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9998
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: colors.primary }}>
              Loading Reports...
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const avgOccupancy = occupancyReport.length > 0
    ? (occupancyReport.reduce((sum, day) => sum + parseFloat(day.occupancy_rate || 0), 0) / occupancyReport.length).toFixed(1)
    : 0;

  const paymentModes = getPaymentModeBreakdown();
  const totalPayments = Object.values(paymentModes).reduce((sum, val) => sum + val, 0);
  const bookingSources = getBookingSourceBreakdown();
  const totalBookings = Object.values(bookingSources).reduce((sum, val) => sum + val, 0);
  const bookingStatuses = getBookingStatusBreakdown();

  // Pie chart renderer
  const renderPieChart = (data, colorMap) => {
    const total = Object.values(data).reduce((sum, val) => sum + val, 0);
    if (total === 0) return null;

    let currentAngle = 0;
    const segments = Object.entries(data).map(([key, value]) => {
      const percentage = (value / total) * 100;
      const angle = (value / total) * 360;
      const segment = {
        key,
        value,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        color: colorMap[key]
      };
      currentAngle += angle;
      return segment;
    });

    return (
      <div>
        <div style={styles.pieChart}>
          <svg width="100%" height="100%" viewBox="0 0 200 200">
            {segments.map((segment, index) => {
              const startAngle = (segment.startAngle - 90) * (Math.PI / 180);
              const endAngle = (segment.endAngle - 90) * (Math.PI / 180);
              const x1 = 100 + 80 * Math.cos(startAngle);
              const y1 = 100 + 80 * Math.sin(startAngle);
              const x2 = 100 + 80 * Math.cos(endAngle);
              const y2 = 100 + 80 * Math.sin(endAngle);
              const largeArc = segment.endAngle - segment.startAngle > 180 ? 1 : 0;

              return (
                <g key={index}>
                  <path
                    d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={segment.color}
                    stroke={colors.white}
                    strokeWidth="2"
                  />
                </g>
              );
            })}
            <circle cx="100" cy="100" r="50" fill={colors.white} />
            <text x="100" y="95" textAnchor="middle" fontSize="24" fontWeight="800" fill={colors.primary}>
              {total}
            </text>
            <text x="100" y="115" textAnchor="middle" fontSize="12" fill={colors.textLight}>
              Total
            </text>
          </svg>
        </div>
        <div style={styles.legend}>
          {segments.filter(s => s.value > 0).map((segment, index) => (
            <div key={index} style={styles.legendItem}>
              <div style={{ ...styles.legendDot, background: segment.color }}></div>
              <span>{segment.key.replace('_', ' ').toUpperCase()}: {segment.percentage.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>📊 Reports & Analytics</h1>
        <p style={{ fontSize: isMobile ? '13px' : '15px', color: colors.textLight, fontWeight: '500' }}>
          Comprehensive insights and performance metrics
        </p>
      </div>

      {/* Main Statistics */}
      <div style={styles.statsGrid}>
        <div
          style={{ ...styles.statCard, borderColor: colors.primary, borderWidth: '2px' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.primary }}>📚</div>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            {revenueData.summary.total_bookings || 0}
          </div>
          <div style={styles.statLabel}>Total Bookings</div>
        </div>

        <div
          style={{ ...styles.statCard, borderColor: colors.success, borderWidth: '2px' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.success }}>💰</div>
          <div style={{ ...styles.statValue, color: colors.success, fontSize: isMobile ? '18px' : '24px' }}>
            ₹{(parseFloat(revenueData.summary.total_revenue || 0) / 1000).toFixed(0)}K
          </div>
          <div style={styles.statLabel}>Total Revenue</div>
        </div>

        <div
          style={{ ...styles.statCard, borderColor: colors.accent, borderWidth: '2px' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.accent }}>💵</div>
          <div style={{ ...styles.statValue, color: colors.accent, fontSize: isMobile ? '18px' : '24px' }}>
            ₹{(parseFloat(revenueData.summary.average_booking_value || 0) / 1000).toFixed(1)}K
          </div>
          <div style={styles.statLabel}>Avg Booking</div>
        </div>

        <div
          style={{ ...styles.statCard, borderColor: colors.warning, borderWidth: '2px' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.warning }}>📈</div>
          <div style={{ ...styles.statValue, color: colors.warning }}>
            {avgOccupancy}%
          </div>
          <div style={styles.statLabel}>Avg Occupancy</div>
        </div>
      </div>

      {/* Payment Mode Breakdown */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <span>💳</span> Payment Mode Analysis
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '30px',
          alignItems: 'center'
        }}>
          <div>
            {renderPieChart(paymentModes, {
              cash: colors.success,
              upi: colors.purple,
              card: colors.info,
              bank_transfer: colors.warning,
              other: colors.mediumGray
            })}
          </div>
          <div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: isMobile ? '13px' : '15px', fontWeight: '600', marginBottom: '12px', color: colors.textDark }}>
                Payment Breakdown
              </div>
              {Object.entries(paymentModes).map(([mode, amount]) => (
                amount > 0 && (
                  <div key={mode} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: colors.lightGray,
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', textTransform: 'capitalize' }}>
                      {mode.replace('_', ' ')}
                    </span>
                    <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: colors.primary }}>
                      ₹{amount.toLocaleString('en-IN')}
                    </span>
                  </div>
                )
              ))}
            </div>
            <div style={{
              padding: '16px',
              background: colors.lightGray,
              borderRadius: '10px',
              borderLeft: `4px solid ${colors.primary}`
            }}>
              <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px' }}>
                Total Collected
              </div>
              <div style={{ fontSize: isMobile ? '24px' : '28px', fontWeight: '800', color: colors.primary }}>
                ₹{totalPayments.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Source Breakdown */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <span>📍</span> Booking Source Distribution
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '30px',
          alignItems: 'center'
        }}>
          <div>
            {renderPieChart(bookingSources, {
              direct: colors.primary,
              channel_manager: colors.purple,
              walk_in: colors.info,
              other: colors.warning
            })}
          </div>
          <div>
            {Object.entries(bookingSources).map(([source, count]) => {
              const percentage = totalBookings > 0 ? (count / totalBookings) * 100 : 0;
              return count > 0 && (
                <div key={source} style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    <span style={{ fontWeight: '700', textTransform: 'capitalize' }}>
                      {source.replace('_', ' ')}
                    </span>
                    <span style={{ fontWeight: '700', color: colors.primary }}>
                      {count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    background: colors.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: '100%',
                      background: colors.primary,
                      transition: 'width 0.5s ease'
                    }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Booking Status Overview */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <span>📋</span> Booking Status Overview
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
          gap: isMobile ? '12px' : '16px'
        }}>
          {Object.entries(bookingStatuses).map(([status, count]) => {
            const statusColors = {
              confirmed: colors.success,
              checked_in: colors.primary,
              checked_out: colors.accent,
              pending: colors.warning,
              cancelled: colors.danger
            };
            return (
              <div key={status} style={{
                background: colors.lightGray,
                padding: isMobile ? '16px' : '20px',
                borderRadius: '12px',
                textAlign: 'center',
                border: `2px solid ${statusColors[status] || colors.mediumGray}`
              }}>
                <div style={{
                  fontSize: isMobile ? '28px' : '36px',
                  fontWeight: '800',
                  color: statusColors[status],
                  marginBottom: '8px'
                }}>
                  {count}
                </div>
                <div style={{
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  color: colors.textLight,
                  letterSpacing: '0.5px'
                }}>
                  {status.replace('_', ' ')}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue by Room Type */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <span>🏨</span> Revenue by Room Type
        </h2>

        {revenueData.byRoomType.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight, fontSize: '15px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📊</div>
            No revenue data available
          </div>
        ) : (
          <div>
            {revenueData.byRoomType.map((type, index) => {
              const maxRevenue = Math.max(...revenueData.byRoomType.map(t => parseFloat(t.revenue)));
              const percentage = (parseFloat(type.revenue) / maxRevenue) * 100;

              const barColors = [colors.primary, colors.success, colors.info, colors.purple, colors.warning];

              return (
                <div key={index} style={{ marginBottom: '18px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    <span style={{ fontWeight: '700' }}>{type.room_type}</span>
                    <span style={{ color: colors.textLight, fontWeight: '600' }}>
                      {type.bookings} bookings
                    </span>
                  </div>
                  <div
                    style={{
                      ...styles.chartBar,
                      width: `${Math.max(percentage, 15)}%`,
                      background: barColors[index % barColors.length]
                    }}
                    onMouseEnter={(e) => !isMobile && (e.currentTarget.style.opacity = '0.8')}
                    onMouseLeave={(e) => !isMobile && (e.currentTarget.style.opacity = '1')}
                  >
                    ₹{parseFloat(type.revenue).toLocaleString('en-IN')}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Occupancy Report */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <span>📅</span> Occupancy Report
        </h2>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: '12px', color: colors.textLight, display: 'block', marginBottom: '6px', fontWeight: '600' }}>
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              style={styles.input}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: colors.textLight, display: 'block', marginBottom: '6px', fontWeight: '600' }}>
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              style={styles.input}
            />
          </div>
        </div>

        {occupancyReport.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight, fontSize: '15px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
            No data available for selected period
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Date</th>
                  <th style={styles.tableHeader}>Occupied</th>
                  {!isMobile && <th style={styles.tableHeader}>Total Rooms</th>}
                  <th style={styles.tableHeader}>Occupancy %</th>
                  <th style={styles.tableHeader}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {occupancyReport.map((day, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>
                      <strong>{new Date(day.date).toLocaleDateString()}</strong>
                    </td>
                    <td style={styles.tableCell}>
                      <strong>{day.rooms_occupied}</strong>
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{day.total_rooms}</td>
                    )}
                    <td style={styles.tableCell}>
                      <div style={{
                        background: `linear-gradient(90deg, ${colors.primary} ${day.occupancy_rate}%, ${colors.lightGray} ${day.occupancy_rate}%)`,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontWeight: '700',
                        fontSize: isMobile ? '11px' : '12px',
                        display: 'inline-block',
                        minWidth: '70px',
                        textAlign: 'center'
                      }}>
                        {parseFloat(day.occupancy_rate).toFixed(1)}%
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <strong style={{ color: colors.primary }}>
                        ₹{parseFloat(day.revenue || 0).toLocaleString('en-IN')}
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Indian vs Foreign Revenue */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: isMobile ? '16px' : '20px',
        padding: isMobile ? '0 8px' : '0'
      }}>
        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>🇮🇳</span> Indian Revenue
          </h2>
          <div style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '800',
            color: colors.primary,
            marginBottom: '12px'
          }}>
            ₹{parseFloat(revenueData.summary.indian_revenue || 0).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: isMobile ? '12px' : '13px', color: colors.textLight, fontWeight: '500' }}>
            From Indian guests this month
          </div>
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: colors.lightGray,
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '4px' }}>
              Percentage of Total
            </div>
            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700', color: colors.success }}>
              {revenueData.summary.total_revenue > 0
                ? ((parseFloat(revenueData.summary.indian_revenue || 0) / parseFloat(revenueData.summary.total_revenue)) * 100).toFixed(1)
                : 0}%
            </div>
          </div>
        </div>

        <div style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>🌍</span> Foreign Revenue
          </h2>
          <div style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '800',
            color: colors.accent,
            marginBottom: '12px'
          }}>
            ₹{parseFloat(revenueData.summary.foreign_revenue || 0).toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: isMobile ? '12px' : '13px', color: colors.textLight, fontWeight: '500' }}>
            From foreign guests this month
          </div>
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: colors.lightGray,
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '4px' }}>
              Percentage of Total
            </div>
            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700', color: colors.info }}>
              {revenueData.summary.total_revenue > 0
                ? ((parseFloat(revenueData.summary.foreign_revenue || 0) / parseFloat(revenueData.summary.total_revenue)) * 100).toFixed(1)
                : 0}%
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default FrontOfficeReports;